import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Checkbox, Col, Flex, Form, Row, Spin } from 'antd'
import { FormInstance } from 'antd/lib'
import { useState } from 'react'

import {
  useClassifierServiceGetCommonClassifierElementListEndpoint,
  useClassifierServiceGetCommonClassifierElementListEndpointKey
} from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  MedicalAssistanceType,
  SystemLanguage
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { Text } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'

import { useStyles } from '../styles'

type Props = {
  form: FormInstance
}

export const Laboratory = ({ form }: Props) => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()

  const [pageNumber, setPageNumber] = useState(1)

  const investigationCategoryIds = Form.useWatch(
    'investigationCategoryIds',
    form
  )
  const medicalAssistanceTypesIds = Form.useWatch(
    'medicalAssistanceTypes',
    form
  )

  const isLaboratoryChecked = medicalAssistanceTypesIds?.includes(
    MedicalAssistanceType.LABORATORY
  )

  const id = useClassifierId(
    ClassifierType.INVESTIGATION_CATEGORY,
    ClassifierCategory.INVESTIGATIONS
  )

  const queryParams = {
    id: String(id),
    pageNumber,
    pageSize: DEFAULT_PER_PAGE
  }

  const {
    data: institutionServicesElements,
    isLoading,
    isFetching
  } = useClassifierServiceGetCommonClassifierElementListEndpoint(
    queryParams,
    [
      useClassifierServiceGetCommonClassifierElementListEndpointKey,
      String(id),
      pageNumber
    ],
    { enabled: !!id && !!isLaboratoryChecked }
  )

  const onChange = (checkedValues: string[]) => {
    const currentPageCategoryIds =
      institutionServicesElements?.data?.map(({ id }) => id) || []

    const previousSelections = investigationCategoryIds?.filter(
      (id: string) => !currentPageCategoryIds.includes(id)
    )

    const updatedIds = [...previousSelections, ...checkedValues]

    form.setFieldValue('investigationCategoryIds', updatedIds)
  }

  return (
    <Form.Item name="investigationCategoryIds">
      {isLaboratoryChecked ? (
        <Checkbox.Group
          className="full-width"
          value={investigationCategoryIds}
          onChange={onChange}
        >
          <Flex vertical gap={8} className="full-width">
            {isLoading || isFetching ? (
              <Flex
                align="center"
                justify="center"
                className={styles.laboratoryLoading}
              >
                <Spin spinning />
              </Flex>
            ) : (
              <Row gutter={[24, 8]}>
                {institutionServicesElements?.data?.map(
                  ({ id, translation }) => (
                    <Col xs={24} key={id}>
                      <Flex align="center" justify="space-between">
                        <Checkbox value={String(id)}>
                          {currentLang?.value === SystemLanguage.RO
                            ? translation?.ro
                            : translation?.ru}
                        </Checkbox>
                      </Flex>
                    </Col>
                  )
                )}
              </Row>
            )}

            <Flex align="center" justify="flex-end" gap={16}>
              <Button
                type="default"
                size="small"
                onClick={() => setPageNumber((prev) => prev - 1)}
                disabled={!institutionServicesElements?.hasPrevious}
                icon={<LeftOutlined />}
              />
              <Text>{pageNumber}</Text>
              <Button
                type="default"
                size="small"
                onClick={() => setPageNumber((prev) => prev + 1)}
                disabled={!institutionServicesElements?.hasNext}
                icon={<RightOutlined />}
              />
            </Flex>
          </Flex>
        </Checkbox.Group>
      ) : (
        <Text>{t('organizational_structure.laboratory_not_selected')}</Text>
      )}
    </Form.Item>
  )
}
